/**
 * One affiliate (/affiliates/:id): who they are, how we work with them,
 * their program numbers, the people they sent, the follow-up moves, their
 * socials, notes and tasks.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, UserRoundX } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { backFromState } from "@/lib/back-target";
import { Button, Skeleton, useConfirm, useToast } from "@/components/ui";
import { Card, Empty, firstName } from "@/features/command/ui";
import { DetailHeader, type AffiliatePatch } from "@/features/affiliates/DetailHeader";
import { MovesCard } from "@/features/affiliates/MovesCard";
import { NotesCard, TasksCard } from "@/features/affiliates/NotesTasks";
import { ProgramCard } from "@/features/affiliates/ProgramCard";
import { ReferredCard } from "@/features/affiliates/ReferredCard";
import { SocialsCard } from "@/features/affiliates/SocialsCard";
import type { Affiliate, AffiliateDetail } from "@/features/affiliates/types";
import { trackRecent } from "@/lib/recent";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function AffiliateDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const location = useLocation();
  const toast = useToast();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const back = backFromState(location.state, { to: "/affiliates", label: "Affiliates" });
  const [data, setData] = useState<AffiliateDetail | null>(null);
  const [missing, setMissing] = useState(false);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    if (!UUID.test(id)) { setMissing(true); return; }
    try {
      const d = await api.get<AffiliateDetail>(`/affiliates/${id}`);
      setData(d);
      trackRecent({ to: `/affiliates/${id}`, label: d.affiliate.name, kind: "affiliate" });
      setMissing(false);
      setFailed(false);
    } catch (err) {
      if (err instanceof ApiError && (err.status === 404 || err.status === 400)) setMissing(true);
      else {
        setFailed(true);
        toast.error(err instanceof ApiError ? err.message : "Couldn't load that affiliate");
      }
    }
  }, [id, toast]);

  useEffect(() => { setData(null); setMissing(false); setFailed(false); void load(); }, [load]);

  const save = useCallback(async (patch: AffiliatePatch) => {
    setData((d) => d && { ...d, affiliate: { ...d.affiliate, ...patch } as AffiliateDetail["affiliate"] });
    try {
      const r = await api.patch<{ affiliate: Affiliate }>(`/affiliates/${id}`, patch);
      setData((d) => d && { ...d, affiliate: { ...d.affiliate, ...r.affiliate } });
      // A new relationship / status replans the moves; a new owner needs their name.
      if (patch.kind || patch.status || patch.hidden !== undefined || patch.owner_id !== undefined) void load();
      if (patch.hidden !== undefined) toast.success(patch.hidden ? "Hidden from the list" : "Back on the list");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't save that");
      void load();
      throw err;
    }
  }, [id, load, toast]);

  const remove = useCallback(async () => {
    const name = data?.affiliate.name ?? "them";
    const ok = await confirm({
      title: `Delete ${name}?`,
      description: "Their notes, socials and follow-ups go too. Tasks linked to them stay on the Work page. This can't be undone.",
      confirmLabel: "Delete",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/affiliates/${id}`);
      toast.success(`Deleted ${name}`);
      navigate(back.to.startsWith("/affiliates/") ? "/affiliates" : back.to, { replace: true });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't delete them");
    }
  }, [back.to, confirm, data?.affiliate.name, id, navigate, toast]);

  const backLink = (
    <Link to={back.to} className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-zinc-100">
      <ArrowLeft className="h-4 w-4" /> {back.label}
    </Link>
  );

  if (missing) {
    return (
      <div className="space-y-5">
        {backLink}
        <Card>
          <Empty
            icon={UserRoundX}
            title="We couldn't find that person"
            action={<Link to="/affiliates"><Button size="sm" variant="secondary">See all affiliates</Button></Link>}
          >
            They may have been merged into someone else, or the link is old.
          </Empty>
        </Card>
      </div>
    );
  }

  if (!data && failed) {
    return (
      <div className="space-y-5">
        {backLink}
        <Card>
          <Empty
            icon={UserRoundX}
            title="Couldn't load them just now"
            action={<Button size="sm" variant="secondary" onClick={() => { setFailed(false); void load(); }}>Try again</Button>}
          >
            Something went wrong on our side. Try again in a moment.
          </Empty>
        </Card>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="space-y-5">
        {backLink}
        <Card className="p-5">
          <div className="flex items-center gap-4">
            <Skeleton className="h-16 w-16 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-4 w-64" />
            </div>
          </div>
          <Skeleton className="mt-5 h-12 w-full rounded-xl" />
        </Card>
        <div className="grid gap-5 lg:grid-cols-3">
          <div className="space-y-5 lg:col-span-2">
            <Skeleton className="h-64 w-full rounded-2xl" />
            <Skeleton className="h-40 w-full rounded-2xl" />
          </div>
          <div className="space-y-5">
            <Skeleton className="h-48 w-full rounded-2xl" />
            <Skeleton className="h-32 w-full rounded-2xl" />
          </div>
        </div>
      </div>
    );
  }

  const a = data.affiliate;
  const first = firstName(a.first_name || a.name);

  return (
    <div className="space-y-5">
      {backLink}
      <DetailHeader a={a} rhythm={data.rhythm} onSave={save} onDelete={a.platform_user_id ? undefined : () => void remove()} />

      <div className="grid items-start gap-5 lg:grid-cols-3">
        <div className="min-w-0 space-y-5 lg:col-span-2">
          <ProgramCard a={a} />
          <MovesCard
            moves={data.moves}
            email={a.email}
            handles={a.handles ?? []}
            onChanged={() => void load()}
            stopped={a.hidden ? "hidden" : a.status === "ended" ? "ended" : null}
          />
          <ReferredCard referred={data.referred} firstName={first} />
        </div>
        <div className="min-w-0 space-y-5">
          <SocialsCard
            affiliateId={a.id}
            handles={a.handles ?? []}
            audience={a.audience}
            onSave={save}
            onAudience={(n) => setData((d) => d && { ...d, affiliate: { ...d.affiliate, audience: n } })}
          />
          <NotesCard notes={a.notes} onSave={(notes) => save({ notes })} />
          <TasksCard affiliateId={a.id} tasks={data.tasks} onChanged={() => void load()} />
        </div>
      </div>
    </div>
  );
}
